import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useContentCheckAssignment } from './hooks/useContentCheckAssignment';
import PolicyAssignmentDialog from './components/PolicyAssignmentDialog';
import PolicyAssignmentList from './components/PolicyAssignmentList';
import PolicyAssignmentHistory from './components/PolicyAssignmentHistory';
import EditAssignmentDialog from './components/EditAssignmentDialog';

/**
 * ContentCheckPolicyManager Component
 *
 * Main component for managing content check policy assignments for a template.
 * Policies assigned at template level cascade to all versions.
 * Provides UI for:
 * - Viewing current assignments
 * - Assigning new policies
 * - Editing existing assignments
 * - Removing assignments
 * - Viewing assignment history
 *
 * @param {string} templateId - Optional template ID prop (for dialog use). If not provided, uses useParams.
 */
const ContentCheckPolicyManager = ({ templateId: templateIdProp }) => {
  const navigate = useNavigate();
  const { templateId: templateIdFromParams } = useParams();
  const templateId = templateIdProp || templateIdFromParams;
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    assignments,
    policies,
    loading,
    policiesLoading,
    error,
    loadAssignments,
    assignPolicy,
    updateAssignment,
    removeAssignment,
    toggleAssignment,
    loadAssignmentHistory,
    setError,
  } = useContentCheckAssignment(templateId);

  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const handleAssign = async config => {
    try {
      await assignPolicy(config.policy_id, config);
      setSuccessMessage('Policy assigned successfully');
      setAssignmentDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message || 'Failed to assign policy');
    }
  };

  const handleEdit = assignment => {
    setSelectedAssignment(assignment);
    setEditDialogOpen(true);
  };

  const handleUpdate = async (assignmentId, updates) => {
    try {
      await updateAssignment(assignmentId, updates);
      setSuccessMessage('Assignment updated successfully');
      setEditDialogOpen(false);
      setSelectedAssignment(null);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message || 'Failed to update assignment');
    }
  };

  const handleDelete = async assignmentId => {
    if (
      window.confirm('Are you sure you want to remove this policy assignment?')
    ) {
      try {
        await removeAssignment(assignmentId);
        setSuccessMessage('Policy assignment removed successfully');
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (err) {
        setError(err.message || 'Failed to remove assignment');
      }
    }
  };

  const handleToggle = async (assignmentId, enabled) => {
    try {
      await toggleAssignment(assignmentId, enabled);
      setSuccessMessage(
        `Assignment ${enabled ? 'enabled' : 'disabled'} successfully`
      );
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message || 'Failed to toggle assignment');
    }
  };

  const handleViewHistory = assignment => {
    setSelectedAssignment(assignment);
    setHistoryDialogOpen(true);
  };

  if (!templateId) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">Template ID is required</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {navigate && (
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Content Check Policy Management
          </Typography>
          {navigate && (
            <Breadcrumbs sx={{ mt: 1 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/templates')}
                sx={{ cursor: 'pointer' }}
              >
                Templates
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate(`/templates/${templateId}`)}
                sx={{ cursor: 'pointer' }}
              >
                Template Details
              </Link>
              <Typography variant="body2" color="text.primary">
                Content Check Policies
              </Typography>
            </Breadcrumbs>
          )}
        </Box>
      </Box>

      {/* Success/Error Alerts */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Actions Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <SecurityIcon />
            Policy Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Policies assigned at template level apply to all versions
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAssignments}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAssignmentDialogOpen(true)}
            disabled={policiesLoading || policies.length === 0}
          >
            Assign Policy
          </Button>
        </Box>
      </Box>

      {/* Assignments List */}
      <PolicyAssignmentList
        assignments={assignments}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggle={handleToggle}
        onViewHistory={handleViewHistory}
        loading={loading}
      />

      {/* Dialogs */}
      <PolicyAssignmentDialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        onAssign={handleAssign}
        policies={policies}
        loading={policiesLoading}
        templateName={templateId}
      />

      <EditAssignmentDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAssignment(null);
        }}
        onUpdate={handleUpdate}
        assignment={selectedAssignment}
      />

      <PolicyAssignmentHistory
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        loadHistory={loadAssignmentHistory}
        templateName={templateId}
      />
    </Container>
  );
};

export default ContentCheckPolicyManager;
